import { useState } from "react";
import { ScreenMax } from "styles/antTheme/Variables";
import { useMediaQuery } from "usehooks-ts";

import { cn } from "@btnet/utilities-core";

import { BdsIconCheckCircle, BdsIconInfoCircle, BdsLink, BdsText } from "@buildertrend/components";

import { queryClient } from "helpers/queryClient";

import { BTLocalStorage } from "types/btStorage";

import { BTAlert } from "commonComponents/btWrappers/BTAlert/BTAlert";
import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import FeedbackWidget from "commonComponents/feedback/FeedbackWidget/FeedbackWidget";

import { FeedbackTarget } from "handlers";

import {
    getCostInboxMigrationStatusGetV2QueryKey,
    useCostInboxMigrationStatusGetV2,
} from "handlers/cost-inbox-migration/cost-inbox-migration";

import "./CostInboxMigrationBanner.less";

import { CostInboxOptInModal } from "./CostInboxOptInModal";

export type CostInboxMigrationBannerVariant = "costInbox" | "bills";

export interface ICostInboxMigrationBannerProps {
    onMigrationComplete?: () => void;
    /** Controls which page variant of the banner to render. Defaults to "costInbox". */
    variant?: CostInboxMigrationBannerVariant;
    className?: string;
}

const loomLink = "https://www.loom.com/share/b1dc7b3001054697b6481af643357a9e";

export const CostInboxMigrationBanner: React.FC<ICostInboxMigrationBannerProps> = ({
    onMigrationComplete,
    variant = "costInbox",
    className,
}) => {
    const [showOptInModal, setShowOptInModal] = useState(false);
    const [showFeedbackWidget, setShowFeedbackWidget] = useState(false);
    const [isDismissed, setIsDismissed] = useState(() =>
        BTLocalStorage.get("bt-boolean-costInboxMigrationBannerDismissed"),
    );
    const isXsScreen = useMediaQuery(`(max-width: ${ScreenMax.xs}px)`);

    const { data: status, isLoading } = useCostInboxMigrationStatusGetV2({
        query: { select: (response) => response.data },
    });

    if (isLoading || !status) {
        // Invisible button to be programatically activated from Chameleon via `document.querySelector('[data-testid="leaveFeedbackButton"]')?.click()`
        return (
            <BTButton
                data-testid="leaveFeedbackButton"
                type="secondary"
                onClick={() => setShowFeedbackWidget(true)}
                style={{ display: "none" }}
            >
                Leave feedback
            </BTButton>
        );
    }

    // Post-switch banner when returning to page after migration

    if (status.isMigrated) {
        if (variant === "bills" && isDismissed) {
            return null;
        }

        const actionButton = (
            <BTRow className="margin-top-sm">
                <BTButton
                    data-testid="learnMoreButton"
                    type="primary"
                    onClick={() => window.open(loomLink)}
                >
                    See what's improved
                </BTButton>
            </BTRow>
        );

        return (
            <>
                <BTAlert
                    title={<BdsText size="distinct-md" text="Cost Inbox now part of Bills" />}
                    message={
                        isXsScreen ? (
                            actionButton
                        ) : (
                            <BdsText size="normal-md">
                                {
                                    "All new bills and receipts appear on the Bills page. Need help migrating your data? "
                                }
                                <BdsLink
                                    testid="emailSarahKate"
                                    to="mailto:sarahkate.hartt@buildertrend.com"
                                    target="_blank"
                                >
                                    Email Sarah Kate
                                </BdsLink>
                            </BdsText>
                        )
                    }
                    closable={variant === "bills"}
                    onClose={() => {
                        BTLocalStorage.set("bt-boolean-costInboxMigrationBannerDismissed", true);
                        setIsDismissed(true);
                    }}
                    type="success"
                    data-testid="costInboxPostMigrationBanner"
                    action={isXsScreen ? undefined : actionButton}
                    showIcon
                    icon={<BdsIconCheckCircle size="md" style={{ alignSelf: "flex-start" }} />}
                    className={cn("margin-bottom-md", className)}
                    style={{ flex: 1 }}
                />
                {showFeedbackWidget && (
                    <FeedbackWidget
                        feedbackTarget={FeedbackTarget.CostInboxMigration}
                        onClose={() => setShowFeedbackWidget(false)}
                    />
                )}
            </>
        );
    }

    const getOptInAlert = () =>
        (() => {
            const buttons = (
                <>
                    <BTButton
                        data-testid="seeWhatsNewButton"
                        type="secondary"
                        className={isXsScreen ? "margin-top-xs" : ""}
                        onClick={() => window.open(loomLink)}
                    >
                        See what's changing
                    </BTButton>
                    <BTButton
                        data-testid="upgradeCostInboxButton"
                        className={!isXsScreen ? "margin-left-sm" : "margin-top-xs"}
                        onClick={() => setShowOptInModal(true)}
                    >
                        Switch to Bills
                    </BTButton>
                </>
            );

            return (
                <BTAlert
                    title={<BdsText size="distinct-md" text="Cost inbox is moving to Bills" />}
                    message={
                        <>
                            <BdsText size="normal-md" className="CostInboxBannerDescription">
                                Explore the new layout — inbox, bulk upload, and email intake — and
                                switch when your team is ready.
                            </BdsText>
                            {isXsScreen && buttons}
                        </>
                    }
                    type="info"
                    data-testid="costInboxOptInBanner"
                    showIcon
                    icon={<BdsIconInfoCircle size="md" />}
                    className={cn("margin-bottom-md", className, {
                        "margin-top-xs": isXsScreen,
                    })}
                    action={!isXsScreen && buttons}
                    style={{ flex: 1 }}
                />
            );
        })();

    if (status.canOptIn && variant === "costInbox" && !isXsScreen) {
        return (
            <>
                {getOptInAlert()}
                {showOptInModal && (
                    <CostInboxOptInModal
                        onClose={() => setShowOptInModal(false)}
                        onOptInComplete={() => {
                            setShowOptInModal(false);
                            void queryClient.invalidateQueries({
                                queryKey: getCostInboxMigrationStatusGetV2QueryKey(),
                            });
                            onMigrationComplete?.();
                        }}
                    />
                )}
            </>
        );
    }

    return null;
};
